/**
 * Service worker registration + update-prompt machinery.
 *
 * Called once from `main.tsx` after React mounts. Kept off the
 * critical render path so a slow registration never blocks first
 * paint. The SW is registered at `${BASE_URL}sw.js` with
 * `scope: BASE_URL`, where `BASE_URL` is Vite's resolved `base`
 * config (production: `/`; per-PR preview deploys: `/pr-<N>/`).
 * The PWA manifest, whose `scope` is hardcoded `/`, is stripped
 * from preview builds via the `reflecto-strip-manifest` Vite
 * plugin so root-scoped install can't fire on a subpath build
 * (see `apps/web/public/manifest.webmanifest` + the plugin block
 * in `apps/web/vite.config.ts`).
 *
 * On unsupported browsers (no `navigator.serviceWorker`, e.g. some
 * private-mode contexts) the function silently no-ops. The PWA
 * install prompt component handles the UX side of "this browser
 * can't install you as an app"; this file is just the registration
 * machinery.
 *
 * Failures are logged via the structured `logWarn` helper rather
 * than thrown — a registration failure shouldn't crash the React
 * tree, the page still works fine over HTTP without a SW. `logWarn`
 * and not `logError` because nothing in this file reports a defect:
 * see the individual `.catch` blocks below, and `log.ts` for the rule.
 *
 * Update flow (web.dev "prompt the user" pattern, mirrors
 * vite-plugin-pwa's `onNeedRefresh`):
 *
 *   1. After register, attach `updatefound` to the registration.
 *      When the installing worker reaches `state === "installed"`
 *      AND a controller already exists, a new build is sitting in
 *      `waiting`. We notify subscribers (the `UpdatePrompt`
 *      banner) so the user sees a "Reload" CTA instead of being
 *      yanked off their current task.
 *   2. `applyUpdate()` posts `{ type: "SKIP_WAITING" }` to the
 *      waiting worker. The SW's `message` handler responds with
 *      `self.skipWaiting()`, which fires `controllerchange` on the
 *      page. We listen once and `location.reload()` so the new
 *      bundle takes over.
 *   3. Periodic `registration.update()` every hour, plus an
 *      opportunistic check on `visibilitychange` so users with the
 *      tab pinned across days still get prompted.
 *
 * The "do nothing on the very first install" guard
 * (`navigator.serviceWorker.controller` is `null` on the page that
 * registered the SW for the first time) is what keeps the banner
 * from flashing on the user's first load.
 */

import { logWarn } from "./log";

type UpdateListener = (available: boolean) => void;

let updateAvailable = false;
let waitingWorker: ServiceWorker | null = null;
let reloading = false;
let updateDetectionWired = false;
let controllerChangeWired = false;
const listeners = new Set<UpdateListener>();

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

export function isUpdateAvailable(): boolean {
  return updateAvailable;
}

export function subscribeUpdateAvailability(listener: UpdateListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * User clicked "Reload" on the update banner. Tells the waiting SW
 * to take over; the `controllerchange` listener installed during
 * registration handles the actual page reload so we don't reload
 * before the new SW is in control (which would just re-fetch the
 * old bundle from cache).
 */
export function applyUpdate(): void {
  if (!waitingWorker) return;
  waitingWorker.postMessage({ type: "SKIP_WAITING" });
}

function setUpdateAvailable(worker: ServiceWorker): void {
  // Only ever flips false → true; there is no "update was retracted"
  // scenario, so the parameter is non-nullable. Call sites already
  // guard against null upstream (the `trackInstallingWorker` early-
  // return on falsy `installing`, the `&&` guard in
  // `wireUpdateDetection`'s already-waiting branch).
  waitingWorker = worker;
  if (updateAvailable) return;
  updateAvailable = true;
  for (const listener of listeners) listener(true);
}

function trackInstallingWorker(registration: ServiceWorkerRegistration): void {
  const installing = registration.installing;
  if (!installing) return;
  installing.addEventListener("statechange", () => {
    if (installing.state !== "installed") return;
    // `controller` is null on the page that did the first-ever
    // registration; we only treat `installed` as an UPDATE when an
    // older SW was already controlling the page. Without this guard
    // the banner would flash on every fresh install.
    if (!navigator.serviceWorker.controller) return;
    setUpdateAvailable(installing);
  });
}

function wireUpdateDetection(registration: ServiceWorkerRegistration): void {
  // Idempotency guard: HMR can re-execute `main.tsx` while the page
  // is already loaded (`document.readyState === "complete"`), which
  // re-runs `registerServiceWorker()` and lands here a second time.
  // Without this guard each HMR cycle would stack another hourly
  // `setInterval` and another `visibilitychange` listener on the
  // same registration. The first wiring is permanent for the page's
  // lifetime — there's no scenario where re-wiring is correct.
  if (updateDetectionWired) return;
  updateDetectionWired = true;

  // A worker may already be sitting in `waiting` from a previous
  // page load that registered the update but never reloaded — pick
  // it up immediately so the banner reappears.
  if (registration.waiting && navigator.serviceWorker.controller) {
    setUpdateAvailable(registration.waiting);
  }

  registration.addEventListener("updatefound", () => {
    trackInstallingWorker(registration);
  });

  // Periodic + visibility-driven update check. `update()` returns a
  // promise that resolves whether or not a new worker was found;
  // any actual change drives the `updatefound` event above.
  const checkForUpdate = (): void => {
    registration.update().catch((err: unknown) => {
      // WARN, not ERROR — and this one is the reason to go looking rather than
      // wait for a Sentry issue to appear. It runs on the hourly interval below
      // AND on every `visibilitychange`, so one tab left open on a flaky
      // connection reports on a loop. An update check that fails is retried by
      // construction; nothing is lost by not reporting it.
      logWarn("ui", "sw_update_check_failed", err);
    });
  };
  setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkForUpdate();
  });
}

function wireControllerChangeReload(): void {
  // Idempotency guard parallel to `wireUpdateDetection`: HMR can
  // re-execute `main.tsx` and re-call `registerServiceWorker()` while
  // the page is still alive, which would otherwise stack a second
  // `controllerchange` listener on `navigator.serviceWorker` every
  // cycle. The shared `reloading` flag protects against double-
  // reload within a single module lifetime, but the listener
  // accumulation alone is enough reason to short-circuit here.
  if (controllerChangeWired) return;
  controllerChangeWired = true;

  // Once we postMessage SKIP_WAITING, the new SW activates and
  // `controllerchange` fires. Reload so the document is served by
  // the new SW (and gets the new index.html / asset URLs). The
  // `reloading` flag prevents an infinite loop if Chrome fires the
  // event twice (it can during DevTools "Update on reload").
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

export function registerServiceWorker(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  // `controllerchange` is set up before registration so we don't
  // miss the event if the new SW activates faster than the page
  // can wire it up afterwards.
  wireControllerChangeReload();

  const register = (): void => {
    // `type: "module"` because the SW is bundled by Vite with ES
    // imports (see `apps/web/vite.config.ts` rollup input). Without
    // this flag the browser tries to load `/sw.js` as a classic
    // worker and chokes on the `import` statement at the top.
    //
    // Module-worker support today: Chrome 89+, Edge 80+, Safari
    // 16.4+. Firefox does NOT yet ship module service workers (the
    // dedicated-worker `type: "module"` is supported but not the
    // SW variant — bug 1360870). On Firefox the registration call
    // rejects, the `.catch` below fires `sw_register_failed`, and
    // the app gracefully degrades to foreground SSE only.
    //
    // Use `BASE_URL` (Vite inlines this from the `base` config at
    // build time) rather than a literal "/sw.js" so per-PR preview
    // builds served under e.g. `/pr-123/` register the SW at the
    // right URL and claim only that scope — a hardcoded "/" scope
    // would either fail the same-origin/scope check or stomp on
    // sibling previews. In production `BASE_URL` is "/", so this
    // is a no-op there.
    navigator.serviceWorker
      .register(`${import.meta.env.BASE_URL}sw.js`, {
        scope: import.meta.env.BASE_URL,
        type: "module",
      })
      .then((registration) => {
        wireUpdateDetection(registration);
      })
      .catch((err: unknown) => {
        // WARN, not ERROR: `logError` forwards to Sentry (lib/log.ts), and every
        // failure here degrades identically — foreground SSE only, as the comment
        // above describes — while none of the observed causes is actionable.
        // Firefox has no module service worker (PWA-4, bug 1360870); a stubbed or
        // headless renderer rejects with a bare "Rejected" (PWA-5). Both are facts
        // about the visitor's browser, and reporting one event per such visitor
        // spent quota to learn nothing.
        //
        // The trade-off, stated so it isn't rediscovered: a genuine regression —
        // a missing or unparseable sw.js — no longer produces a Sentry event
        // either. That failure is not silent, though. It breaks EVERY visitor at
        // once, so it shows up as background push delivery going to zero, which is
        // a louder signal than a per-visitor error would have been.
        logWarn("ui", "sw_register_failed", err);
      });
  };

  // Defer until after the load event so the SW registration network
  // request doesn't compete with the initial bundle download.
  //
  // `load` is a one-shot event — listeners attached after it fires
  // are silently dropped. In normal page loads `main.tsx` (a module
  // script) runs during HTML parse, well before `load`, so the
  // listener wins the race. Edge cases (HMR re-runs, certain
  // hydration-after-load embed contexts) can land here with
  // `readyState === "complete"` already, so guard the immediate
  // path too.
  if (document.readyState === "complete") {
    register();
  } else {
    window.addEventListener("load", register, { once: true });
  }
}
